import React, { useState, useEffect } from "react";
import axios from "axios";
import { ShieldCheck, Users, Check, X, RefreshCw, AlertCircle, Clock } from "lucide-react";
import { Button } from "./ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminMasterPanel = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);
  const [payments, setPayments] = useState([]);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem("cortoviral_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const [res, pay] = await Promise.all([
        axios.get(`${API}/admin/users`, { headers, withCredentials: true }),
        axios.get(`${API}/admin/payments`, { headers, withCredentials: true })
      ]);
      setUsers(res.data);
      setPayments(pay.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleToggleUnlock = async (userId) => {
    setUpdatingId(userId);
    try {
      const token = localStorage.getItem("cortoviral_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API}/admin/users/${userId}/toggle-unlock`, {}, { headers, withCredentials: true });
      fetchUsers();
    } catch (e) {
      console.error(e);
    } finally {
      setUpdatingId(null);
    }
  };

  const handlePayment = async (txId, decision) => {
    setUpdatingId(txId);
    try {
      const token = localStorage.getItem("cortoviral_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API}/admin/payments/${txId}/${decision}`, {}, { headers, withCredentials: true });
      fetchUsers();
    } catch (e) {
      console.error(e);
    } finally {
      setUpdatingId(null);
    }
  };

  const pending = payments.filter((p) => p.status === "pending");

  return (
    <div className="bg-[#12141D] border border-red-500/30 rounded-2xl p-6 shadow-2xl">
      <div className="flex items-center justify-between mb-6 pb-4 border-b border-white/10">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-red-600/20 text-red-500 flex items-center justify-center font-bold">
            <ShieldCheck className="w-6 h-6" />
          </div>
          <div>
            <h3 className="text-lg font-black text-white">Panel de Control Maestro (Admin)</h3>
            <p className="text-xs text-slate-400">
              Gestión de creadores, auditoría de multicuentas por IP y desbloqueo de licencias PayPal
            </p>
          </div>
        </div>

        <Button
          data-testid="admin-refresh-users-btn"
          variant="secondary"
          size="sm"
          onClick={fetchUsers}
          className="text-xs flex items-center gap-1.5"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Actualizar
        </Button>
      </div>

      <div className="mb-6 p-4 rounded-xl bg-amber-500/5 border border-amber-500/20">
        <h4 className="text-sm font-bold text-amber-300 mb-1">Pagos pendientes de revisar ({pending.length})</h4>
        <p className="text-[11px] text-slate-400 mb-3">
          Busca el ID de transacción en tu PayPal (Actividad). Si el dinero ha llegado, pulsa Aprobar; si no, Rechazar.
        </p>
        {pending.length === 0 ? (
          <p className="text-xs text-slate-500">No hay pagos pendientes.</p>
        ) : (
          <div className="space-y-2">
            {pending.map((p) => (
              <div key={p.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-3 rounded-lg bg-black/30 border border-white/5 text-xs">
                <div className="min-w-0">
                  <div className="font-semibold text-white truncate">{p.user_email}</div>
                  <div className="font-mono text-amber-300 break-all">ID PayPal: {p.paypal_order_id}</div>
                  <div className="text-[10px] text-slate-500">{new Date(p.created_at).toLocaleString("es-ES")} · {p.amount}€</div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button size="sm" disabled={updatingId === p.id} onClick={() => handlePayment(p.id, "approve")}
                    className="h-7 px-3 text-[11px] bg-emerald-600 hover:bg-emerald-500 text-white font-bold">
                    <Check className="w-3.5 h-3.5 mr-1" /> Aprobar
                  </Button>
                  <Button size="sm" variant="secondary" disabled={updatingId === p.id} onClick={() => handlePayment(p.id, "reject")}
                    className="h-7 px-3 text-[11px]">
                    <X className="w-3.5 h-3.5 mr-1" /> Rechazar
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-left text-xs">
          <thead>
            <tr className="border-b border-white/10 text-slate-400 uppercase tracking-wider font-mono">
              <th className="py-3 px-4">Usuario</th>
              <th className="py-3 px-4">Rol</th>
              <th className="py-3 px-4">IP Red / Hash</th>
              <th className="py-3 px-4">Estado 48h / PayPal</th>
              <th className="py-3 px-4">Créditos IA</th>
              <th className="py-3 px-4 text-right">Acción Maestro</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5 text-slate-300">
            {users.map((u) => {
              const isMaster = u.role === "admin";
              return (
                <tr key={u.id} className="hover:bg-white/[0.02] transition-colors">
                  <td className="py-3 px-4">
                    <div className="font-semibold text-white">{u.name || "Sin nombre"}</div>
                    <div className="text-[11px] text-slate-500">{u.email}</div>
                  </td>
                  <td className="py-3 px-4 font-mono">
                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${isMaster ? "bg-red-500/20 text-red-400 border border-red-500/30" : "bg-slate-800 text-slate-300"}`}>
                      {u.role}
                    </span>
                  </td>
                  <td className="py-3 px-4 font-mono text-[11px] text-slate-400">
                    <div>{u.ip_raw || "127.0.0.1"}</div>
                    <div className="text-[10px] text-slate-600 truncate max-w-[120px]">{u.ip_hash}</div>
                  </td>
                  <td className="py-3 px-4">
                    {isMaster ? (
                      <span className="text-emerald-400 font-semibold text-[11px]">Exento (Maestro)</span>
                    ) : u.has_paid ? (
                      <span className="text-emerald-400 font-semibold text-[11px] flex items-center gap-1">
                        <Check className="w-3.5 h-3.5" /> Pagado
                      </span>
                    ) : (
                      <span className="text-amber-400 font-semibold text-[11px] flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5" /> {u.access_status?.hours_left || 0}h prueba
                      </span>
                    )}
                  </td>
                  <td className="py-3 px-4 font-mono">
                    {u.daily_credits ?? 10} / día
                  </td>
                  <td className="py-3 px-4 text-right">
                    {!isMaster && (
                      <Button
                        data-testid={`admin-toggle-user-${u.id}`}
                        size="sm"
                        variant={u.has_paid ? "secondary" : "default"}
                        disabled={updatingId === u.id}
                        onClick={() => handleToggleUnlock(u.id)}
                        className={`text-[11px] h-7 px-3 ${u.has_paid ? "border-white/10" : "bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold"}`}
                      >
                        {updatingId === u.id ? (
                          <RefreshCw className="w-3 h-3 animate-spin" />
                        ) : u.has_paid ? (
                          "Revocar Acceso"
                        ) : (
                          "Aprobar y Desbloquear"
                        )}
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
